import { create } from "zustand";
import { childLook, DEFAULT_LOOK, expandLook, lookFromQuery, rotateAround } from "../engine/look";
import { spawnNetwork } from "../engine/network";
import { shortTitle } from "../engine/titles";
import type { OrbitLayer, SpatialNode, WorldLook } from "../engine/types";

const SCALE_INNER = 380;
const SCALE_MIDDLE = 520;
const SCALE_OUTER = 680;
const LERP = 0.04;

function hashStr(s: string) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Unique slice of 2π per child index — 360° pie around the parent. */
export function v1RadialOffset(_layer: OrbitLayer, index: number, count: number, depth: number, seed: number) {
  const n = Math.max(1, count);
  const angle = (index / n) * Math.PI * 2;
  const radius = 420 * Math.pow(0.72, Math.max(0, depth));
  const offsetZ = (((seed % 21) - 10) / 10) * 80;
  return {
    x: Math.cos(angle) * radius,
    y: Math.sin(angle) * radius,
    z: offsetZ,
  };
}

function mapChildren(
  raw: { id?: string; name: string; metricLabel: string; orbitLayer: OrbitLayer; colorHex: string; cluster?: string; signalLabel?: string; why?: string; relation?: string }[],
  parent: SpatialNode,
): SpatialNode[] {
  const now = performance.now();
  const depth = parent.generationDepth + 1;
  const total = Math.max(1, raw.length);
  return raw.map((n, i) => {
    const layer = n.orbitLayer;
    const seed = hashStr(`${parent.id}-${n.id || n.name}-${i}`);
    const rest = v1RadialOffset(layer, i, total, depth, seed);
    const look = childLook(parent.look, n.colorHex || parent.look.atmosphere, layer, i);
    look.radius *= Math.pow(0.62, depth);
    return {
      id: `sn-${parent.id}-${n.id || i}-${seed.toString(16)}`,
      signalId: n.id,
      name: n.name,
      metricLabel: n.metricLabel || "",
      orbitLayer: layer,
      color: n.colorHex || parent.look.atmosphere,
      cluster: n.cluster || n.relation || layer,
      signalLabel: n.signalLabel || "",
      why: n.why || "",
      relation: n.relation || layer,
      parentId: parent.id,
      parentX: parent.x,
      parentY: parent.y,
      parentZ: parent.z,
      generationDepth: depth,
      x: parent.x,
      y: parent.y,
      z: parent.z,
      targetX: parent.x + rest.x,
      targetY: parent.y + rest.y,
      targetZ: parent.z + rest.z,
      orbitRadius: Math.hypot(rest.x, rest.y, rest.z),
      orbitAngle: 0,
      orbitSpeed: 0,
      spawnedAt: now + i * 70,
      rest,
      axis: { x: 0, y: 1, z: 0 },
      look,
    };
  });
}

function breadcrumbsFrom(nodes: SpatialNode[], focusId: string | null) {
  const trail: string[] = [];
  let id = focusId;
  while (id) {
    const n = nodes.find((x) => x.id === id);
    if (!n) break;
    trail.unshift(n.name);
    id = n.parentId || null;
  }
  return ["Home", ...trail];
}

export type EngineStore = {
  nodes: SpatialNode[];
  focusId: string | null;
  selectedId: string | null;
  isGenerating: boolean;
  breadcrumbs: string[];
  ingest: (query: string, origin?: { x: number; y: number; z: number; look?: WorldLook }) => Promise<boolean>;
  expand: (nodeId: string) => Promise<boolean>;
  pulseFrom: (query: string) => Promise<boolean>;
  select: (nodeId: string | null) => void;
  zoomOut: () => void;
  focusBreadcrumb: (index: number) => void;
  reset: () => void;
  step: () => void;
};

export const useEngineStore = create<EngineStore>((set, get) => ({
  nodes: [],
  focusId: null,
  selectedId: null,
  isGenerating: false,
  breadcrumbs: ["Home"],

  ingest: async (query, origin) => {
    const { nodes, focusId } = get();
    if (nodes.length && focusId) return get().expand(focusId);

    set({ isGenerating: true });
    const result = await spawnNetwork(query, 0, undefined, []);
    if (!result.nodes.length) {
      set({ isGenerating: false });
      return false;
    }
    const x = origin?.x ?? 0;
    const y = origin?.y ?? 0;
    const z = origin?.z ?? 0;
    const look = expandLook(origin?.look || lookFromQuery(query));
    const rootId = `sn-root-${result.rootId}`;
    const root: SpatialNode = {
      id: rootId,
      signalId: result.rootId,
      name: shortTitle(query),
      metricLabel: "PARENT",
      orbitLayer: "inner",
      color: look.atmosphere,
      cluster: "origin",
      signalLabel: "PARENT",
      why: "",
      relation: "ORIGIN",
      parentId: "",
      parentX: x,
      parentY: y,
      parentZ: z,
      generationDepth: 0,
      x,
      y,
      z,
      targetX: x,
      targetY: y,
      targetZ: z,
      orbitRadius: 0,
      orbitAngle: 0,
      orbitSpeed: 0,
      spawnedAt: performance.now() - 1200,
      rest: { x: 0, y: 0, z: 0 },
      axis: { x: 0, y: 1, z: 0 },
      look,
    };
    const children = mapChildren(result.nodes, root);
    set({
      nodes: [root, ...children],
      focusId: rootId,
      selectedId: rootId,
      breadcrumbs: breadcrumbsFrom([root, ...children], rootId),
      isGenerating: false,
    });
    return true;
  },

  pulseFrom: async (query) => {
    const { nodes, focusId, ingest, expand } = get();
    if (!nodes.length || !focusId) return ingest(query);
    const parent = nodes.find((n) => n.id === focusId);
    if (!parent) return ingest(query);
    set({ isGenerating: true, selectedId: focusId, focusId });
    const visited = nodes.map((n) => n.signalId).filter(Boolean) as string[];
    const result = await spawnNetwork(query, parent.generationDepth + 1, parent.signalId, visited);
    if (!result.nodes.length) {
      set({ isGenerating: false });
      return false;
    }
    const kids = mapChildren(result.nodes, parent);
    set((s) => {
      const next = [...s.nodes, ...kids];
      return {
        nodes: next,
        isGenerating: false,
        breadcrumbs: breadcrumbsFrom(next, focusId),
      };
    });
    return true;
  },

  expand: async (nodeId) => {
    const { nodes } = get();
    const parent = nodes.find((n) => n.id === nodeId);
    if (!parent) return false;
    if (nodes.some((n) => n.parentId === nodeId)) {
      set({ focusId: nodeId, selectedId: nodeId, breadcrumbs: breadcrumbsFrom(nodes, nodeId) });
      return true;
    }
    set({ isGenerating: true, focusId: nodeId, selectedId: nodeId });
    const visited = nodes.map((n) => n.signalId).filter(Boolean) as string[];
    const result = await spawnNetwork(parent.name, parent.generationDepth + 1, parent.signalId, visited);
    if (typeof console !== "undefined") {
      console.log("1. PULSE FIRED ON:", parent.id, parent.name);
      console.log("2. ALGORITHM RETURNED:", result.nodes.length, result.nodes.map((n) => n.name));
    }
    if (!result.nodes.length) {
      set({ isGenerating: false, breadcrumbs: breadcrumbsFrom(nodes, nodeId) });
      return false;
    }
    const kids = mapChildren(result.nodes, parent);
    set((s) => {
      const next = [...s.nodes, ...kids];
      return {
        nodes: next,
        isGenerating: false,
        focusId: nodeId,
        selectedId: nodeId,
        breadcrumbs: breadcrumbsFrom(next, nodeId),
      };
    });
    return true;
  },

  select: (nodeId) => {
    const { nodes } = get();
    set({ selectedId: nodeId, focusId: nodeId || get().focusId, breadcrumbs: breadcrumbsFrom(nodes, nodeId || get().focusId) });
  },

  zoomOut: () => {
    const { nodes, focusId } = get();
    const focus = nodes.find((n) => n.id === focusId);
    if (!focus?.parentId) return;
    set({ focusId: focus.parentId, selectedId: focus.parentId, breadcrumbs: breadcrumbsFrom(nodes, focus.parentId) });
  },

  focusBreadcrumb: (index) => {
    const { nodes, focusId } = get();
    if (index <= 0) {
      const root = nodes.find((n) => !n.parentId);
      if (root) set({ focusId: root.id, selectedId: root.id, breadcrumbs: breadcrumbsFrom(nodes, root.id) });
      return;
    }
    const trail: SpatialNode[] = [];
    let id = focusId;
    while (id) {
      const n = nodes.find((x) => x.id === id);
      if (!n) break;
      trail.unshift(n);
      id = n.parentId || null;
    }
    const target = trail[index - 1];
    if (target) set({ focusId: target.id, selectedId: target.id, breadcrumbs: breadcrumbsFrom(nodes, target.id) });
  },

  reset: () =>
    set({
      nodes: [],
      focusId: null,
      selectedId: null,
      isGenerating: false,
      breadcrumbs: ["Home"],
    }),

  step: () => {
    const { nodes } = get();
    if (!nodes.length) return;
    const byId = new Map(nodes.map((n) => [n.id, n]));
    for (const node of nodes) {
      const parent = node.parentId ? byId.get(node.parentId) : undefined;
      const px = parent ? parent.x : node.parentX;
      const py = parent ? parent.y : node.parentY;
      const pz = parent ? parent.z : node.parentZ;
      node.parentX = px;
      node.parentY = py;
      node.parentZ = pz;
      const t = Math.min(1, Math.max(0, (performance.now() - node.spawnedAt) / 1200));
      const p = 1 - Math.pow(1 - t, 3);
      if (p < 1) {
        node.x = px + node.rest.x * p;
        node.y = py + node.rest.y * p;
        node.z = pz + node.rest.z * p;
      } else {
        node.orbitAngle += node.orbitSpeed;
        const spun = rotateAround(node.rest, node.axis, node.orbitAngle);
        node.targetX = px + spun.x;
        node.targetY = py + spun.y;
        node.targetZ = pz + spun.z;
        node.x += (node.targetX - node.x) * LERP;
        node.y += (node.targetY - node.y) * LERP;
        node.z += (node.targetZ - node.z) * LERP;
      }
    }
  },
}));
